// 🔍 DIAGNOSTIC (posé le 2026-10-23, à retirer une fois l'enquête close —
// cf. mémoire regie-gel-safari-akinax-2026-10-22).
//
// Le 22/10, la régie sous Safari s'est figée sans la moindre trace, après 43 min
// d'un lecteur hls.js « fantôme » (`lecture 0.00 s`). Safari ne donne aucune
// mesure de mémoire : on compte donc les objets qui la consomment. Une ligne
// [RESSOURCES] toutes les 30 s ; un nombre qui ne fait que monter au fil de la
// session désigne ce qui fuit.
//
// Les compteurs propres à un module (hls.js, captures canvas, gardes de gel)
// s'inscrivent via registerProbeGauge : la sonde n'importe aucun de ces modules.

import { DIAGNOSTIC_PROBES_ENABLED } from '../../backendServices/debugManagment/remoteLogger';

const REPORT_MS = 30_000;

// Réactivée le 2026-10-26 (enquête sur les trous d'image de la régie, direct AKINAX) SANS rallumer
// DIAGNOSTIC_PROBES_ENABLED : cet interrupteur général active aussi [AV-SYNC], qui coûte plusieurs
// points du fil principal — justement ce qu'on cherche à mesurer. Cette sonde-ci ne fait qu'une ligne
// toutes les 30 s et incrémenter un compteur par image affichée.
// Coupée le 2026-10-29, enquête qualité close : repasser à true pour la rallumer.
const RESOURCE_PROBE_ENABLED = false;

const gauges = new Map<string, () => number | string>();

export function registerProbeGauge(name: string, read: () => number | string): void {
  gauges.set(name, read);
}

// ── RTCPeerConnection : comptées à la construction, jaugées à la lecture ──────
const peers = new Set<WeakRef<RTCPeerConnection>>();

function trackPeerConnections(): void {
  const Native = (window as any).RTCPeerConnection as typeof RTCPeerConnection | undefined;
  if (!Native || (Native as any).__resourceProbe) return;
  const Wrapped: any = function (this: any, ...args: any[]) {
    const pc = new (Native as any)(...args);
    peers.add(new WeakRef(pc));
    return pc;
  };
  Wrapped.prototype = Native.prototype;
  Wrapped.__resourceProbe = true;
  for (const k of Object.getOwnPropertyNames(Native)) {
    if (k === 'prototype' || k === 'length' || k === 'name') continue;
    try { Wrapped[k] = (Native as any)[k]; } catch { /* statique en lecture seule */ }
  }
  (window as any).RTCPeerConnection = Wrapped;
}

function peerStats(): string {
  let open = 0, closed = 0, liveTracks = 0;
  for (const ref of Array.from(peers)) {
    const pc = ref.deref();
    if (!pc) { peers.delete(ref); continue; }
    if (pc.connectionState === 'closed' || pc.signalingState === 'closed') { closed++; continue; }
    open++;
    try {
      for (const s of pc.getSenders()) if (s.track?.readyState === 'live') liveTracks++;
      for (const r of pc.getReceivers()) if (r.track?.readyState === 'live') liveTracks++;
    } catch { /* pc fermée entre-temps */ }
  }
  return `${open} ouvertes/${closed} fermées, ${liveTracks} pistes vivantes`;
}

function videoStats(): string {
  const vs = Array.from(document.querySelectorAll('video'));
  let playing = 0, deadSrc = 0, withStream = 0;
  for (const v of vs) {
    if (!v.paused && !v.ended) playing++;
    if (v.srcObject) withStream++;
    if (v.readyState === 0 && !v.srcObject && !v.currentSrc) deadSrc++;
  }
  return `${vs.length} (${playing} en lecture, ${withStream} srcObject, ${deadSrc} vides)`;
}

let started = false;

export function startResourceProbe(): void {
  if (started || !(RESOURCE_PROBE_ENABLED || DIAGNOSTIC_PROBES_ENABLED)) return;
  started = true;
  trackPeerConnections();

  // Cadence d'affichage du navigateur. Un onglet masqué, une fenêtre entièrement recouverte ou un
  // fil principal saturé font tomber requestAnimationFrame — et avec lui le canvas de la régie, donc
  // la vidéo envoyée au serveur (trou d'image du 26/10 : le son continuait, l'image à 0 i/s).
  // On échantillonne toutes les 5 s et on rapporte le PIRE échantillon de la période.
  let rafCount = 0;
  const countFrame = () => { rafCount++; requestAnimationFrame(countFrame); };
  requestAnimationFrame(countFrame);
  let worstRafRate = Infinity;
  let lastSampleAt = performance.now();
  setInterval(() => {
    const now = performance.now();
    const rate = rafCount / Math.max(0.001, (now - lastSampleAt) / 1000);
    rafCount = 0;
    lastSampleAt = now;
    worstRafRate = Math.min(worstRafRate, rate);
  }, 5_000);

  // Chaque changement de visibilité ou de focus est tracé tout de suite, avec son heure : c'est la
  // pièce qui manquait pour rapprocher un trou d'image d'un onglet quitté.
  document.addEventListener('visibilitychange', () => {
    console.warn(`[RESSOURCES] visibilité de l'onglet → ${document.visibilityState}`);
  });
  window.addEventListener('blur',  () => console.warn('[RESSOURCES] fenêtre sans focus'));
  window.addEventListener('focus', () => console.warn('[RESSOURCES] fenêtre au premier plan'));

  const t0 = performance.now();
  setInterval(() => {
    const parts: string[] = [
      `t+${Math.round((performance.now() - t0) / 60_000)} min`,
      `<video> DOM: ${videoStats()}`,
      `<canvas> DOM: ${document.querySelectorAll('canvas').length}`,
      `rAF pire: ${Number.isFinite(worstRafRate) ? Math.round(worstRafRate) : '?'}/s`,
      `onglet: ${document.visibilityState}${document.hasFocus() ? '' : ' sans focus'}`,
      `RTCPeerConnection: ${peerStats()}`,
    ];
    for (const [name, read] of Array.from(gauges)) {
      try { parts.push(`${name}: ${read()}`); } catch { parts.push(`${name}: ?`); }
    }
    // Chrome seulement ; Safari ne l'expose pas, et la ligne le dit.
    const mem = (performance as any).memory;
    parts.push(mem
      ? `tas JS: ${Math.round(mem.usedJSHeapSize / 1048576)}/${Math.round(mem.jsHeapSizeLimit / 1048576)} Mo`
      : 'tas JS: indisponible');
    console.warn(`[RESSOURCES] ${parts.join(' | ')}`);
    worstRafRate = Infinity;
  }, REPORT_MS);
}
